import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Box, Text, Title, Card, Stack, Loader, Tabs, Button, Group, Badge, Skeleton } from '@mantine/core'
import agent, { type LeaderboardEntryDto } from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { PostCard, EmptyState, AuthGateCta } from '../../app/common/ui'
import AmmoScoreBadge from '../../app/common/ui/AmmoScoreBadge'
import type { Post } from '../../app/models/post'
import type { TagDto } from '../../app/models/tag'
import { canCreateCommunityPost } from '../../app/common/constants/reputation'

function getInitials (displayName?: string | null, username?: string) {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return displayName.slice(0, 2).toUpperCase()
  }
  if (username?.trim()) return username.trim().slice(0, 2).toUpperCase()
  return '?'
}

function PostSkeleton () {
  return (
    <Box
      sx={{
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        padding: 16
      }}
    >
      <Group spacing="sm" mb="sm">
        <Skeleton circle height={32} />
        <Box sx={{ flex: 1 }}>
          <Skeleton height={10} width="40%" mb={6} />
          <Skeleton height={8} width="25%" />
        </Box>
      </Group>
      <Skeleton height={14} mb={8} />
      <Skeleton height={14} width="80%" mb={8} />
      <Skeleton height={10} width="35%" />
    </Box>
  )
}

const mapPostToCard = (p: Post) => (
  <PostCard
    key={p.id}
    id={p.id}
    title={p.title}
    excerpt={p.excerpt}
    type={p.type}
    coverImageUrl={p.coverImageUrl}
    mainPhotoUrl={p.mainPhotoUrl}
    authorUsername={p.authorUsername}
    authorDisplayName={p.authorDisplayName}
    authorImage={p.authorImage}
    authorTier={p.authorAmmoTier}
    authorScore={p.authorAmmoScore}
    voteScore={p.voteScore ?? 0}
    currentUserVote={p.currentUserVote}
    commentCount={p.comments?.length ?? 0}
    createdAt={p.createdAt}
    tags={p.tags}
    variant="default"
  />
)

/** Legacy community trending/following feed; route `/legacy-following-feed` (not linked from primary nav). */
export default observer(function LegacyFollowingFeedPage () {
  const { userStore, feedStore } = useStore()
  const history = useHistory()
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntryDto[]>([])
  const [trendingTags, setTrendingTags] = useState<TagDto[]>([])
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  useEffect(() => {
    feedStore.loadTrending(10, 7)
  }, [feedStore])

  useEffect(() => {
    if (userStore.isLoggedIn) feedStore.loadFollowingFeed(20)
  }, [userStore.isLoggedIn, feedStore])

  useEffect(() => {
    agent.Profiles.leaderboard(5)
      .then(setLeaderboard)
      .catch(() => setLeaderboard([]))
  }, [])

  useEffect(() => {
    agent.Tags.list()
      .then((tags) => {
        const sorted = [...tags].sort((a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0)).slice(0, 10)
        setTrendingTags(sorted)
      })
      .catch(() => setTrendingTags([]))
  }, [])

  // IntersectionObserver for infinite scroll sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          feedStore.loadMoreFollowing(20)
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [feedStore, feedStore.followingHasMore])

  const handleTagToggle = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    setSelectedTagIds(next)
    feedStore.loadFollowingFeed(20, next)
  }

  const trendingPosts = feedStore.trending?.posts ?? []

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 300px',
        gap: 16,
        padding: '16px 24px',
        maxWidth: 1440,
        margin: '0 auto',
        '@media (max-width: 768px)': { gridTemplateColumns: '1fr' }
      }}
    >
      <Box>
        <Group position="apart" mb="md">
          <Title order={2} sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 20 }}>
            COMMUNITY FEED
          </Title>
          {!userStore.isLoggedIn
            ? (
            <Button component={Link} to="/register" color="ember" size="sm" variant="light">
              Join to post
            </Button>
              )
            : canCreateCommunityPost(userStore.user)
              ? (
            <Button component={Link} to="/createPost" color="ember" size="sm">
              + New Post
            </Button>
                )
              : (
            <Text size="xs" color="dimmed" sx={{ maxWidth: 200, textAlign: 'right' }}>
              Earn 10 AmmoScore to publish posts
            </Text>
                )}
        </Group>

        <Tabs defaultValue="trending">
          <Tabs.List>
            <Tabs.Tab value="trending">Trending</Tabs.Tab>
            <Tabs.Tab value="following">Following</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="trending" pt="md">
            {feedStore.loadingTrending
              ? (
              <Loader />
                )
              : (
              <Stack spacing="sm">
                {trendingPosts.map(mapPostToCard)}
                {trendingPosts.length > 0 && (
                  <Button variant="light" color="gray" onClick={() => feedStore.loadTrending(20, 7)}>
                    Load more
                  </Button>
                )}
                {!feedStore.loadingTrending && trendingPosts.length === 0 && (
                  <Text size="sm" color="dimmed">
                    No trending posts in the last 7 days.
                  </Text>
                )}
              </Stack>
                )}
          </Tabs.Panel>

          <Tabs.Panel value="following" pt="md">
            {/* Tag filter chips — shown when logged in and tags are available */}
            {userStore.isLoggedIn && trendingTags.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 6,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                  paddingBottom: 10,
                  borderBottom: '1px solid var(--gs-border)'
                }}
              >
                {trendingTags.map((tag) => {
                  const active = selectedTagIds.includes(tag.id.toString())
                  return (
                    <Box
                      key={tag.id}
                      onClick={() => handleTagToggle(tag.id.toString())}
                      sx={{
                        padding: '3px 10px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: 'DM Sans, sans-serif',
                        cursor: 'pointer',
                        border: `1px solid ${active ? 'var(--gs-accent)' : 'var(--gs-border)'}`,
                        background: active ? 'rgba(232,93,4,0.15)' : 'var(--gs-surface2)',
                        color: active ? '#E85D04' : 'var(--gs-muted)',
                        transition: 'all 0.15s',
                        '&:hover': {
                          borderColor: 'var(--gs-accent)',
                          color: '#E85D04'
                        }
                      }}
                    >
                      {tag.displayName || tag.name}
                    </Box>
                  )
                })}
                {selectedTagIds.length > 0 && (
                  <Box
                    onClick={() => { setSelectedTagIds([]); feedStore.loadFollowingFeed(20, []) }}
                    sx={{
                      padding: '3px 10px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'DM Sans, sans-serif',
                      cursor: 'pointer',
                      border: '1px solid #f85149',
                      background: 'rgba(248,81,73,0.1)',
                      color: '#f85149'
                    }}
                  >
                    Clear
                  </Box>
                )}
              </Box>
            )}

            {!userStore.isLoggedIn ? (
              <AuthGateCta
                action="see posts from people you follow"
                pitch="Follow collectors, builders, and enthusiasts — their posts appear here in real time."
                bullets={[
                  'Follow other gun owners and see their posts',
                  'Build your own feed based on what you care about',
                  'Share your builds and reviews to earn AmmoScore'
                ]}
              />
            ) : feedStore.loadingFollowing ? (
              <Stack spacing="sm">
                <PostSkeleton />
                <PostSkeleton />
                <PostSkeleton />
              </Stack>
            ) : feedStore.followingItems.length === 0 ? (
              <EmptyState
                title="Your following feed is empty"
                message="Find people to follow on the Discover page."
                action={{ label: 'Discover Users →', onClick: () => history.push('/discover') }}
              />
            ) : (
              <Stack spacing="sm">
                {feedStore.followingItems.map(mapPostToCard)}

                {/* Infinite scroll sentinel */}
                {feedStore.followingHasMore && (
                  <div ref={sentinelRef} style={{ height: 1 }} />
                )}

                {/* Loading more skeletons */}
                {feedStore.loadingMore && (
                  <>
                    <PostSkeleton />
                    <PostSkeleton />
                    <PostSkeleton />
                  </>
                )}

                {/* All caught up */}
                {!feedStore.followingHasMore && feedStore.followingItems.length > 0 && (
                  <Box py="lg" sx={{ textAlign: 'center' }}>
                    <Text size="sm" color="dimmed">
                      You're all caught up.
                    </Text>
                  </Box>
                )}
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Box>

      <Box
        sx={{
          '@media (max-width: 768px)': { order: -1 }
        }}
      >
        <Stack spacing="md">
          <Card padding="md" withBorder sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)' }}>
            <Text weight={600} size="sm" sx={{ fontFamily: 'Oswald, sans-serif', marginBottom: 12 }}>
              TOP CONTRIBUTORS
            </Text>
            {leaderboard.length === 0
              ? (
              <Text size="xs" color="dimmed">
                —
              </Text>
                )
              : (
              <Stack spacing="xs">
                {leaderboard.map((u) => (
                  <Group key={u.username} spacing="sm">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: u.mainPhotoUrl ? `url(${u.mainPhotoUrl}) center/cover` : 'var(--gs-surface2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--gs-text)'
                      }}
                    >
                      {!u.mainPhotoUrl && getInitials(u.displayName, u.username)}
                    </Box>
                    <Text size="xs" component={Link} to={`/profiles/${u.username}`} sx={{ color: 'var(--gs-text)', textDecoration: 'none' }}>
                      {u.username}
                    </Text>
                    <AmmoScoreBadge tier={u.ammoTier} score={u.ammoScore} size="sm" />
                  </Group>
                ))}
              </Stack>
                )}
          </Card>

          <Card padding="md" withBorder sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)' }}>
            <Text weight={600} size="sm" sx={{ fontFamily: 'Oswald, sans-serif', marginBottom: 12 }}>
              TRENDING TAGS
            </Text>
            {trendingTags.length === 0
              ? (
              <Text size="xs" color="dimmed">
                —
              </Text>
                )
              : (
              <Group spacing="xs">
                {trendingTags.map((t) => (
                  <Badge
                    key={t.id}
                    component={Link}
                    to={`/blog?tagIds=${t.id}`}
                    variant="outline"
                    sx={{ cursor: 'pointer', textDecoration: 'none' }}
                  >
                    {t.displayName || t.name}
                  </Badge>
                ))}
              </Group>
                )}
          </Card>

          <Card padding="md" withBorder sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)' }}>
            <Text weight={600} size="sm" sx={{ fontFamily: 'Oswald, sans-serif', marginBottom: 12 }}>
              QUICK LINKS
            </Text>
            <Stack spacing="xs">
              <Button component={Link} to="/registry" variant="outline" size="xs" sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)' }}>
                Registry
              </Button>
              <Button component={Link} to="/parts-bin" variant="outline" size="xs" sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)' }}>
                Parts Bin
              </Button>
              <Button component={Link} to="/builds" variant="outline" size="xs" sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)' }}>
                Builds
              </Button>
            </Stack>
          </Card>
        </Stack>
      </Box>
    </Box>
  )
})
